'use client'

/**
 * Publishes the base route/entity surface into the workspace registry
 * (Issue #12948).
 *
 * Mounted once, by the root layout, INSIDE `WorkspaceContextProvider` and
 * beside the application pane. It renders nothing.
 *
 * This is the component that makes "registrations are removed on navigation"
 * true without anyone writing cleanup code: `usePageContext()` re-derives on
 * every pathname change, `useRegisterWorkspaceSurface` re-publishes when the
 * serialized bytes differ, and the previous route's registration is replaced
 * rather than accumulated because both share `WORKSPACE_ROUTE_SURFACE_ID`.
 *
 * Page-owned adapters get the same guarantee from unmounting: a Next.js
 * navigation unmounts the old page subtree, the adapters inside it run their
 * effect cleanups, and their registrations disappear. Nothing polls for
 * staleness.
 */

import { useMemo } from 'react'
import { usePageContext } from '@/components/ai-chat/use-page-context'
import { useRegisterWorkspaceSurface } from '@/contexts/workspace-context'
import { buildRouteWorkspaceSurface } from '@/lib/workspace-context/route-surface'

export function WorkspaceRouteRegistrar() {
  const pageContext = usePageContext()
  const snapshot = useMemo(
    () => buildRouteWorkspaceSurface(pageContext),
    [pageContext],
  )

  useRegisterWorkspaceSurface(snapshot)

  return null
}
